import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

// ── Modifier Builder ──────────────────────────────────────────────────────
const ModifierBuilder = ({ modifiers, onChange }) => {
  const addGroup = () => onChange([...modifiers, { title: '', required: false, multi_select: false, options: [] }]);
  const removeGroup = (gi) => onChange(modifiers.filter((_, i) => i !== gi));
  const updateGroup = (gi, key, value) => onChange(modifiers.map((g, i) => i === gi ? { ...g, [key]: value } : g));
  const addOption = (gi) => onChange(modifiers.map((g, i) => i === gi ? { ...g, options: [...g.options, { name: '', extra_price: 0 }] } : g));
  const removeOption = (gi, oi) => onChange(modifiers.map((g, i) => i === gi ? { ...g, options: g.options.filter((_, j) => j !== oi) } : g));
  const updateOption = (gi, oi, key, value) => onChange(modifiers.map((g, i) => i === gi ? { ...g, options: g.options.map((o, j) => j === oi ? { ...o, [key]: value } : o) } : g));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-gray-700">Modifiers / Add-ons</Label>
        <button type="button" onClick={addGroup} className="flex items-center gap-1 text-xs text-[#E65100] font-semibold hover:underline">
          <Plus className="w-3.5 h-3.5" /> Add Group
        </button>
      </div>

      {modifiers.length === 0 && (
        <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
          No modifiers yet. Add groups like "Spice Level", "Add-ons", "Size" etc.
        </p>
      )}

      {modifiers.map((group, gi) => (
        <div key={gi} className="border border-gray-200 rounded-xl p-3 space-y-2 bg-gray-50">
          <div className="flex items-center gap-2">
            <Input placeholder="Group name (e.g. Spice Level, Add-ons)" value={group.title} onChange={e => updateGroup(gi, 'title', e.target.value)} className="flex-1 text-sm h-8" />
            <button type="button" onClick={() => removeGroup(gi)} className="text-red-400 hover:text-red-600 p-1"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox" checked={group.required} onChange={e => updateGroup(gi, 'required', e.target.checked)} className="accent-[#E65100]" />
              Required
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox" checked={group.multi_select} onChange={e => updateGroup(gi, 'multi_select', e.target.checked)} className="accent-[#E65100]" />
              Allow multiple
            </label>
          </div>
          <div className="space-y-1.5 pl-1">
            {group.options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <Input placeholder="Option name (e.g. Extra Cheese)" value={opt.name} onChange={e => updateOption(gi, oi, 'name', e.target.value)} className="flex-1 text-sm h-7" />
                <div className="relative flex-shrink-0 w-24">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">+₹</span>
                  <Input type="number" min="0" step="0.5" placeholder="0" value={opt.extra_price} onChange={e => updateOption(gi, oi, 'extra_price', parseFloat(e.target.value) || 0)} className="text-sm h-7 pl-6" />
                </div>
                <button type="button" onClick={() => removeOption(gi, oi)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button type="button" onClick={() => addOption(gi)} className="text-xs text-[#E65100] hover:underline font-medium pl-1">+ Add option</button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Main MenuManagement ───────────────────────────────────────────────────
const MenuManagement = () => {
  const { restaurantId } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', price: '', image_url: '', category: '', is_available: true, is_veg: true, modifiers: [] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [menuRes, catRes] = await Promise.all([api.getMenu(restaurantId), api.getCategories(restaurantId)]);
      setMenuItems(menuRes.data);
      setCategories(catRes.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, price: parseFloat(formData.price) };
      if (editingItem) { await api.updateMenuItem(editingItem.id, data); toast.success('Menu item updated'); }
      else { await api.createMenuItem(data); toast.success('Menu item created'); }
      setIsDialogOpen(false); resetForm(); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save menu item'); }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, description: item.description, price: item.price.toString(), image_url: item.image_url, category: item.category, is_available: item.is_available !== false, is_veg: item.is_veg !== false, modifiers: item.modifiers || [] });
    setIsDialogOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try { await api.deleteMenuItem(itemId); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete menu item'); }
  };

  const resetForm = () => { setFormData({ name: '', description: '', price: '', image_url: '', category: '', is_available: true, is_veg: true, modifiers: [] }); setEditingItem(null); };
  const handleDialogClose = (open) => { setIsDialogOpen(open); if (!open) resetForm(); };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div><div className="h-8 w-48 bg-gray-200 rounded-lg mb-2" /><div className="h-4 w-64 bg-gray-100 rounded" /></div>
            <div className="h-10 w-32 bg-gray-200 rounded-xl" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="h-40 bg-gray-200" />
                <div className="p-4 space-y-2"><div className="h-5 w-3/4 bg-gray-200 rounded" /><div className="h-4 w-1/2 bg-gray-100 rounded" /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Menu Management</h2>
            <p className="text-gray-600 mt-1">Manage your restaurant menu items</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button className="bg-[#E65100] hover:bg-[#D84800] text-white" data-testid="add-menu-item-button"><Plus className="w-5 h-5 mr-2" />Add Menu Item</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div><Label htmlFor="name">Name</Label><Input id="name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required data-testid="menu-item-name-input" /></div>
                <div><Label htmlFor="description">Description</Label><Textarea id="description" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required rows={3} data-testid="menu-item-description-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label htmlFor="price">Price (₹)</Label><Input id="price" type="number" step="0.01" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} required data-testid="menu-item-price-input" /></div>
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select value={formData.category} onValueChange={value => setFormData({ ...formData, category: value })} required>
                      <SelectTrigger data-testid="menu-item-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>{categories.map(cat => <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="image_url">Image URL</Label>
                  <Input id="image_url" type="url" value={formData.image_url} onChange={e => setFormData({ ...formData, image_url: e.target.value })} required data-testid="menu-item-image-input" />
                  {formData.image_url && <img src={formData.image_url} alt="Preview" className="mt-2 w-full h-48 object-cover rounded-lg" />}
                </div>

                {/* Veg toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${formData.is_veg ? 'border-green-600' : 'border-red-600'}`}><span className={`w-2 h-2 rounded-full ${formData.is_veg ? 'bg-green-600' : 'bg-red-600'}`} /></span>
                    <div><p className="font-medium text-gray-900 text-sm">Food Type</p><p className="text-xs text-gray-500 mt-0.5">{formData.is_veg ? 'Vegetarian' : 'Non-Vegetarian'}</p></div>
                  </div>
                  <button type="button" onClick={() => setFormData({ ...formData, is_veg: !formData.is_veg })} className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${formData.is_veg ? 'bg-green-500' : 'bg-red-500'}`}>
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${formData.is_veg ? 'translate-x-8' : 'translate-x-1'}`} />
                  </button>
                </div>

                {/* Availability toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div><p className="font-medium text-gray-900 text-sm">Item Availability</p><p className="text-xs text-gray-500 mt-0.5">{formData.is_available ? 'Customers can order this item' : 'Item is hidden as out of stock'}</p></div>
                  <button type="button" onClick={() => setFormData({ ...formData, is_available: !formData.is_available })} className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${formData.is_available ? 'bg-green-500' : 'bg-red-400'}`}>
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${formData.is_available ? 'translate-x-8' : 'translate-x-1'}`} />
                  </button>
                </div>

                {/* Modifiers */}
                <div className="border-t border-gray-100 pt-4">
                  <ModifierBuilder modifiers={formData.modifiers} onChange={mods => setFormData({ ...formData, modifiers: mods })} />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-[#E65100] hover:bg-[#D84800]" data-testid="save-menu-item-button">{editingItem ? 'Update' : 'Create'} Item</Button>
                  <Button type="button" variant="outline" onClick={() => handleDialogClose(false)} data-testid="cancel-menu-item-button">Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {menuItems.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center"><p className="text-gray-500">No menu items yet. Click "Add Menu Item" to get started.</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menuItems.map(item => (
              <div key={item.id} className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition ${item.is_available === false ? 'border-red-200 opacity-75' : 'border-gray-100'}`} data-testid={`menu-item-card-${item.id}`}>
                <div className="relative">
                  <img src={item.image_url} alt={item.name} className={`w-full h-48 object-cover ${item.is_available === false ? 'grayscale' : ''}`} />
                  {item.is_available === false && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full"><AlertTriangle className="w-3 h-3" />Out of Stock</div>
                  )}
                  {item.modifiers?.length > 0 && (
                    <div className="absolute top-2 right-2 bg-[#E65100] text-white text-xs font-bold px-2 py-1 rounded-full">{item.modifiers.length} modifier{item.modifiers.length > 1 ? 's' : ''}</div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${item.is_veg !== false ? 'border-green-600' : 'border-red-600'}`}><span className={`w-2 h-2 rounded-full ${item.is_veg !== false ? 'bg-green-600' : 'bg-red-600'}`} /></span>
                        <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{item.category}</p>
                    </div>
                    <span className="text-xl font-bold text-[#E65100]">₹{item.price.toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                  {item.modifiers?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.modifiers.map((g, i) => <span key={i} className="text-xs bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded-full">{g.title || 'Group'}</span>)}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(item)} className="flex-1" data-testid={`edit-menu-item-${item.id}-button`}><Edit2 className="w-4 h-4 mr-1" />Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-menu-item-${item.id}-button`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuManagement;
